<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { useZuwadAssistantStore } from 'dashboard/stores/zuwadAssistant';
import { groupThreads } from '../assistantFormat';

defineProps({
  activeId: { type: Number, default: null },
});
const emit = defineEmits(['open', 'rename', 'remove']);

const { t } = useI18n();
const store = useZuwadAssistantStore();
const query = ref('');
// Collapsed sections and people, by key; everything starts open.
const collapsed = ref({});
const toggle = key => {
  collapsed.value = { ...collapsed.value, [key]: !collapsed.value[key] };
};

const filtered = computed(() => {
  const text = query.value.trim().toLowerCase();
  if (!text) return store.threads;
  return store.threads.filter(
    thread =>
      (thread.title || '').toLowerCase().includes(text) ||
      (thread.owner?.name || '').toLowerCase().includes(text)
  );
});
const sections = computed(() =>
  groupThreads({
    threads: filtered.value,
    teams: store.teams,
    myTeamIds: store.myTeamIds,
    supervisor: store.supervisor,
  })
);
const showSearch = computed(() => store.supervisor || store.threads.length > 8);

const timeLabel = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  const today = date.toDateString() === new Date().toDateString();
  return today
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { day: 'numeric', month: 'short' });
};
const initials = name =>
  (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0">
    <div v-if="showSearch" class="px-2 pt-2">
      <input
        v-model="query"
        type="search"
        dir="auto"
        :placeholder="t('ZUWAD_ASSISTANT.SEARCH')"
        class="w-full h-8 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 outline-none focus:border-n-brand !mb-0"
      />
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto p-2 space-y-3">
      <p
        v-if="store.loaded && !store.threads.length"
        class="p-3 text-sm text-n-slate-11"
      >
        {{ t('ZUWAD_ASSISTANT.NO_CHATS') }}
      </p>

      <section v-for="section in sections" :key="section.key">
        <button
          type="button"
          class="flex items-center w-full gap-1.5 px-2 py-1 text-xs font-medium uppercase tracking-wide text-n-slate-11 hover:text-n-slate-12"
          @click="toggle(section.key)"
        >
          <span
            :class="
              collapsed[section.key]
                ? 'i-lucide-chevron-right rtl:rotate-180'
                : 'i-lucide-chevron-down'
            "
            class="size-3.5 shrink-0"
          />
          <span
            v-if="section.kind === 'team'"
            class="i-lucide-users size-3.5 shrink-0"
          />
          <span dir="auto" class="truncate">
            {{
              section.kind === 'mine'
                ? t('ZUWAD_ASSISTANT.MY_CHATS')
                : section.kind === 'none'
                  ? t('ZUWAD_ASSISTANT.NO_TEAM')
                  : section.name
            }}
          </span>
          <span class="ms-auto text-n-slate-10 normal-case">
            {{
              section.kind === 'mine' ? section.threads.length : section.count
            }}
          </span>
        </button>

        <template v-if="!collapsed[section.key]">
          <ul v-if="section.kind === 'mine'" class="mt-1 space-y-0.5">
            <li
              v-if="!section.threads.length"
              class="px-3 py-1.5 text-xs text-n-slate-10"
            >
              {{ t('ZUWAD_ASSISTANT.NO_OWN_CHATS') }}
            </li>
            <li
              v-for="thread in section.threads"
              :key="thread.id"
              class="group flex items-center gap-1 rounded-lg ps-3 pe-1 py-2 cursor-pointer text-sm"
              :class="
                thread.id === activeId
                  ? 'bg-n-blue-3 text-n-blue-12 font-medium'
                  : 'text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="emit('open', thread.id)"
            >
              <span dir="auto" class="flex-1 truncate">{{
                thread.title || t('ZUWAD_ASSISTANT.UNTITLED')
              }}</span>
              <span
                v-if="thread.unread && thread.id !== activeId"
                class="rounded-full size-2 shrink-0 bg-n-brand"
                :aria-label="t('ZUWAD_ASSISTANT.UNREAD')"
              />
              <span
                class="text-xxs text-n-slate-10 shrink-0 group-hover:hidden"
                >{{
                  timeLabel(thread.last_message_at || thread.created_at)
                }}</span
              >
              <Button
                icon="i-lucide-pencil"
                variant="ghost"
                color="slate"
                size="xs"
                class="hidden group-hover:inline-flex"
                :aria-label="t('ZUWAD_ASSISTANT.RENAME')"
                @click.stop="emit('rename', thread)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                color="ruby"
                size="xs"
                class="hidden group-hover:inline-flex"
                :aria-label="t('ZUWAD_ASSISTANT.DELETE')"
                @click.stop="emit('remove', thread)"
              />
            </li>
          </ul>

          <div v-else class="mt-1 space-y-1">
            <div
              v-for="person in section.people"
              :key="`${section.key}-${person.owner.id}`"
              class="rounded-lg border border-n-weak"
            >
              <button
                type="button"
                class="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-n-slate-12"
                @click="toggle(`${section.key}-${person.owner.id}`)"
              >
                <span
                  class="inline-grid place-items-center rounded-full size-6 shrink-0 text-xxs font-medium bg-n-alpha-2 text-n-slate-12"
                  >{{ initials(person.owner.name) }}</span
                >
                <span dir="auto" class="flex-1 truncate text-start">{{
                  person.owner.name
                }}</span>
                <span class="text-xs text-n-slate-10">{{
                  person.threads.length
                }}</span>
                <span
                  :class="
                    collapsed[`${section.key}-${person.owner.id}`]
                      ? 'i-lucide-chevron-right rtl:rotate-180'
                      : 'i-lucide-chevron-down'
                  "
                  class="size-3.5 shrink-0 text-n-slate-10"
                />
              </button>
              <ul
                v-if="!collapsed[`${section.key}-${person.owner.id}`]"
                class="pb-1 space-y-0.5"
              >
                <li
                  v-for="thread in person.threads"
                  :key="thread.id"
                  class="group flex items-center gap-1 mx-1 rounded-md ps-7 pe-1 py-1.5 cursor-pointer text-sm"
                  :class="
                    thread.id === activeId
                      ? 'bg-n-blue-3 text-n-blue-12 font-medium'
                      : 'text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  @click="emit('open', thread.id)"
                >
                  <span dir="auto" class="flex-1 truncate">{{
                    thread.title || t('ZUWAD_ASSISTANT.UNTITLED')
                  }}</span>
                  <span
                    class="text-xxs text-n-slate-10 shrink-0"
                    :class="{ 'group-hover:hidden': thread.can_manage }"
                    >{{
                      timeLabel(thread.last_message_at || thread.created_at)
                    }}</span
                  >
                  <template v-if="thread.can_manage">
                    <Button
                      icon="i-lucide-pencil"
                      variant="ghost"
                      color="slate"
                      size="xs"
                      class="hidden group-hover:inline-flex"
                      :aria-label="t('ZUWAD_ASSISTANT.RENAME')"
                      @click.stop="emit('rename', thread)"
                    />
                    <Button
                      icon="i-lucide-trash-2"
                      variant="ghost"
                      color="ruby"
                      size="xs"
                      class="hidden group-hover:inline-flex"
                      :aria-label="t('ZUWAD_ASSISTANT.DELETE')"
                      @click.stop="emit('remove', thread)"
                    />
                  </template>
                </li>
              </ul>
            </div>
          </div>
        </template>
      </section>
    </div>
  </div>
</template>
